import { useEffect, useState } from 'react';
import {
  CalendarDays,
  Plus,
  Loader2,
  Trash2,
  Play,
  Lock,
  Unlock,
} from 'lucide-react';
import { apiFetch } from './apiBase';
import HelpTip from './HelpTip';
import { getReconSettings, saveReconSettings } from './reconSettings';
import {
  currentYearMonth,
  formatPeriodCode,
  loadLocalPeriods,
  saveLocalPeriods,
  mergeLocalPeriods,
} from './payrollPeriodStore';

const EMPTY_DRAFT = {
  code: '',
  name: '',
  startDate: '',
  endDate: '',
  status: 'open',
};

function newLocalPeriod(draft, code) {
  const now = new Date().toISOString();
  return {
    id: `local:${code}`,
    code,
    name: draft.name.trim(),
    startDate: draft.startDate,
    endDate: draft.endDate,
    status: draft.status === 'closed' ? 'closed' : 'open',
    source: 'managed',
    runCount: 0,
    latestRun: null,
    createdAt: now,
    updatedAt: now,
  };
}

export default function PayrollPeriods({ onNavigate }) {
  const [periods, setPeriods] = useState([]);
  const [mongo, setMongo] = useState(true);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentCode, setCurrentCode] = useState(() => getReconSettings().payPeriod);
  const [busyId, setBusyId] = useState('');

  const reload = async () => {
    try {
      const data = await apiFetch('/api/payroll-periods');
      const server = data.periods || [];
      const usesMongo = data.mongo !== false;
      setMongo(usesMongo);
      setPeriods(mergeLocalPeriods(server));
      return { server, mongo: usesMongo };
    } catch {
      setMongo(false);
      setPeriods(loadLocalPeriods());
      return { server: [], mongo: false };
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      await reload();
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const applyPeriodForRecon = (code) => {
    saveReconSettings({ payPeriod: code });
    setCurrentCode(code);
    setSuccess(`Period ${formatPeriodCode(code)} is set for Payroll Recon. Upload the register and run reports for this cycle.`);
    setError('');
  };

  const addPeriod = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      const body = {
        code: draft.code.trim(),
        name: draft.name.trim(),
        startDate: draft.startDate,
        endDate: draft.endDate,
        status: draft.status,
      };
      try {
        const { period } = await apiFetch('/api/payroll-periods', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        setDraft(EMPTY_DRAFT);
        setShowForm(false);
        await reload();
        if (period?.code) applyPeriodForRecon(period.code);
        setSuccess(
          `Period ${formatPeriodCode(period?.code || body.code)} added. Next: upload the ADP register and run Payroll Recon for this cycle.`
        );
      } catch (err) {
        if (err.status === 503) {
          const code = String(body.code || '').replace(/\D/g, '');
          if (!/^\d{6}$/.test(code)) {
            throw new Error('Enter a pay period like 202610 (year and month).');
          }
          const next = [newLocalPeriod(draft, code), ...loadLocalPeriods().filter((p) => p.code !== code)];
          saveLocalPeriods(next);
          setDraft(EMPTY_DRAFT);
          setShowForm(false);
          await reload();
          applyPeriodForRecon(code);
          setSuccess(
            `Period ${formatPeriodCode(code)} added. Next: upload the ADP register and run Payroll Recon for this cycle.`
          );
        } else {
          throw err;
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (period, status) => {
    setBusyId(period.id);
    setError('');
    try {
      if (mongo && !String(period.id).startsWith('run:') && !String(period.id).startsWith('local:')) {
        await apiFetch(`/api/payroll-periods/${period.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ status }),
        });
      } else if (String(period.id).startsWith('local:') || !mongo) {
        saveLocalPeriods(
          loadLocalPeriods().map((p) => (p.code === period.code ? { ...p, status } : p))
        );
      } else {
        throw new Error('This period comes from a past recon run and cannot be edited.');
      }
      await reload();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId('');
    }
  };

  const removePeriod = async (period) => {
    if (!window.confirm(`Remove period ${formatPeriodCode(period.code)} from the list?`)) return;
    setBusyId(period.id);
    setError('');
    try {
      if (mongo && !String(period.id).startsWith('run:') && !String(period.id).startsWith('local:')) {
        await apiFetch(`/api/payroll-periods/${period.id}`, { method: 'DELETE' });
      } else if (String(period.id).startsWith('local:') || !mongo) {
        saveLocalPeriods(loadLocalPeriods().filter((p) => p.code !== period.code));
      } else {
        throw new Error('This period comes from a past recon run and cannot be removed here.');
      }
      await reload();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusyId('');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-16 text-slate-400">
        <Loader2 className="h-5 w-5 animate-spin" />
        Loading payroll periods…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="page-hero">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <p className="section-title">Payroll</p>
            <h2 className="mt-1 text-2xl font-bold tracking-tight text-slate-100">Payroll periods</h2>
            <p className="mt-2 max-w-2xl text-sm text-slate-400">
              Add the pay cycle you are reconciling (for example 202610). Then upload the ADP register
              and run reports for that period. Results stay grouped by the same cycle.
            </p>
            {currentCode && (
              <p className="mt-3 text-xs text-slate-500">
                Current cycle for recon:{' '}
                <span className="font-medium text-slate-300">{formatPeriodCode(currentCode)}</span>
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() => {
              setShowForm(true);
              setError('');
              setSuccess('');
            }}
            disabled={showForm}
            className="btn-primary shrink-0"
          >
            <Plus className="h-4 w-4" />
            Add period
          </button>
        </div>
      </header>

      {error && <div className="alert-warning">{error}</div>}
      {success && <div className="alert-success">{success}</div>}

      {showForm && (
        <form onSubmit={addPeriod} className="card-panel space-y-4 p-5">
          <h3 className="font-semibold text-slate-100">Add a payroll period</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-sm">
              <span className="font-medium text-slate-300">
                Period <HelpTip term="payPeriod" />
              </span>
              <input
                className="input-field font-mono"
                placeholder={currentYearMonth()}
                value={draft.code}
                onChange={(e) => setDraft({ ...draft, code: e.target.value })}
                required
              />
              <span className="mt-1 block text-xs text-slate-500">Year and month, like 202610</span>
            </label>
            <label className="block text-sm">
              <span className="font-medium text-slate-300">Name (optional)</span>
              <input
                className="input-field"
                placeholder="PP10 or Biweekly 10"
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              />
            </label>
            <label className="block text-sm">
              <span className="font-medium text-slate-300">Start date (optional)</span>
              <input
                type="date"
                className="input-field"
                value={draft.startDate}
                onChange={(e) => setDraft({ ...draft, startDate: e.target.value })}
              />
            </label>
            <label className="block text-sm">
              <span className="font-medium text-slate-300">End date (optional)</span>
              <input
                type="date"
                className="input-field"
                value={draft.endDate}
                onChange={(e) => setDraft({ ...draft, endDate: e.target.value })}
              />
            </label>
          </div>
          <fieldset>
            <legend className="text-sm font-medium text-slate-300">Status</legend>
            <div className="mt-2 flex flex-wrap gap-3 text-sm text-slate-300">
              <label className="inline-flex items-center gap-2">
                <input
                  type="radio"
                  name="periodStatus"
                  checked={draft.status === 'open'}
                  onChange={() => setDraft({ ...draft, status: 'open' })}
                />
                Open
              </label>
              <label className="inline-flex items-center gap-2">
                <input
                  type="radio"
                  name="periodStatus"
                  checked={draft.status === 'closed'}
                  onChange={() => setDraft({ ...draft, status: 'closed' })}
                />
                Closed
              </label>
            </div>
          </fieldset>
          <div className="flex flex-wrap gap-2">
            <button type="submit" disabled={saving} className="btn-primary">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Save period
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                setShowForm(false);
                setDraft(EMPTY_DRAFT);
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {periods.length === 0 && !showForm ? (
        <div className="empty-state-panel">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-800 ring-1 ring-slate-700">
            <CalendarDays className="h-7 w-7 text-cyan-500/80" />
          </div>
          <h3 className="mt-4 text-lg font-semibold text-slate-100">No payroll periods yet</h3>
          <p className="mt-2 max-w-md text-sm text-slate-400">
            Add a period to start recon. After you save the cycle, upload the ADP register and run
            Payroll Recon — Results will fill in for that period.
          </p>
          <button type="button" onClick={() => setShowForm(true)} className="btn-primary mt-6">
            <Plus className="h-4 w-4" />
            Add period
          </button>
        </div>
      ) : periods.length > 0 ? (
        <div className="card-panel overflow-hidden">
          <table className="data-table w-full text-sm">
            <thead className="text-xs uppercase">
              <tr>
                <th className="px-4 py-2 text-left">Period</th>
                <th className="px-4 py-2 text-left">Dates</th>
                <th className="px-4 py-2 text-left">Status</th>
                <th className="px-4 py-2 text-left">Results</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {periods.map((p) => {
                const current = p.code === currentCode;
                const dates =
                  p.startDate || p.endDate
                    ? [p.startDate, p.endDate].filter(Boolean).join(' – ')
                    : '—';
                const canEdit = p.source === 'managed' || String(p.id).startsWith('local:');
                return (
                  <tr key={p.id}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-100">
                        {p.name || `Pay period ${formatPeriodCode(p.code)}`}
                      </div>
                      <div className="font-mono text-xs text-slate-500">{p.code}</div>
                      {current && (
                        <span className="badge-success mt-1 inline-block text-[10px]">Using for recon</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-400">{dates}</td>
                    <td className="px-4 py-3">
                      <span className={p.status === 'closed' ? 'badge-neutral' : 'badge-success'}>
                        {p.status === 'closed' ? 'Closed' : 'Open'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-400">
                      {p.runCount > 0 ? (
                        <span>
                          {p.runCount} run{p.runCount === 1 ? '' : 's'}
                          {p.latestRun?.exceptionCount != null
                            ? ` · ${p.latestRun.exceptionCount} exceptions`
                            : ''}
                        </span>
                      ) : (
                        <span className="text-slate-500">Not run yet</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap justify-end gap-1">
                        <button
                          type="button"
                          className="btn-secondary py-1 text-xs"
                          onClick={() => applyPeriodForRecon(p.code)}
                        >
                          <Play className="h-3 w-3" />
                          Use for recon
                        </button>
                        {p.runCount > 0 && (
                          <button
                            type="button"
                            className="btn-secondary py-1 text-xs"
                            onClick={() => onNavigate?.('results')}
                          >
                            View results
                          </button>
                        )}
                        {canEdit && (
                          <button
                            type="button"
                            className="btn-secondary py-1 text-xs"
                            disabled={busyId === p.id}
                            onClick={() => setStatus(p, p.status === 'closed' ? 'open' : 'closed')}
                          >
                            {p.status === 'closed' ? (
                              <Unlock className="h-3 w-3" />
                            ) : (
                              <Lock className="h-3 w-3" />
                            )}
                            {p.status === 'closed' ? 'Reopen' : 'Close'}
                          </button>
                        )}
                        {canEdit && (
                          <button
                            type="button"
                            className="btn-secondary py-1 text-xs"
                            disabled={busyId === p.id}
                            onClick={() => removePeriod(p)}
                            aria-label={`Remove period ${p.code}`}
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : null}

      {periods.length > 0 && (
        <p className="text-sm text-slate-400">
          After you add a period, go to{' '}
          <button type="button" className="link-accent" onClick={() => onNavigate?.('reports')}>
            Reports
          </button>{' '}
          to pull Oracle files, then{' '}
          <button type="button" className="link-accent" onClick={() => onNavigate?.('recon')}>
            Payroll Recon
          </button>{' '}
          to upload the ADP register and run the cycle.
        </p>
      )}
    </div>
  );
}
